import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const DriverLocationPage = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams?.get('booking_id');
  const vehicleNumber = searchParams?.get('vehicle');
  const driverName = searchParams?.get('driver') || 'Driver';
  const driverPhone = searchParams?.get('phone') || '';

  const [status, setStatus] = useState('idle'); // idle | requesting | sharing | error | stopped
  const [errorMsg, setErrorMsg] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [coords, setCoords] = useState(null);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  const sendLocation = useCallback(async (position) => {
    if (!bookingId || !vehicleNumber) return;
    const { latitude, longitude, accuracy, speed, heading } = position?.coords;
    try {
      await supabase?.rpc('upsert_truck_location', {
        p_booking_id: bookingId,
        p_vehicle_number: vehicleNumber,
        p_latitude: latitude,
        p_longitude: longitude,
        p_accuracy: accuracy || null,
        p_speed: speed || null,
        p_heading: heading || null,
        p_driver_name: driverName,
        p_driver_phone: driverPhone,
      });
      setLastUpdate(new Date());
      setCoords({ latitude, longitude, accuracy, speed });
    } catch (err) {
      console.error('Location send error:', err);
    }
  }, [bookingId, vehicleNumber, driverName, driverPhone]);

  const startSharing = useCallback(() => {
    if (!navigator.geolocation) {
      setErrorMsg('GPS not supported on this device.');
      setStatus('error');
      return;
    }
    setStatus('requesting');
    navigator.geolocation?.getCurrentPosition(
      (pos) => {
        setStatus('sharing');
        sendLocation(pos);
        // Watch position continuously
        watchIdRef.current = navigator.geolocation?.watchPosition(
          sendLocation,
          (err) => {
            console.warn('GPS watch error:', err?.message);
          },
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
        );
      },
      (err) => {
        setErrorMsg(`GPS permission denied: ${err?.message}`);
        setStatus('error');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, [sendLocation]);

  const stopSharing = useCallback(() => {
    if (watchIdRef?.current !== null) {
      navigator.geolocation?.clearWatch(watchIdRef?.current);
      watchIdRef.current = null;
    }
    if (intervalRef?.current) {
      clearInterval(intervalRef?.current);
      intervalRef.current = null;
    }
    setStatus('stopped');
  }, []);

  useEffect(() => {
    return () => {
      if (watchIdRef?.current !== null) {
        navigator.geolocation?.clearWatch(watchIdRef?.current);
      }
      if (intervalRef?.current) clearInterval(intervalRef?.current);
    };
  }, []);

  if (!bookingId || !vehicleNumber) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Invalid Link</h2>
          <p className="text-sm text-gray-500">This tracking link is missing required information. Please use the link sent to you via WhatsApp.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold">SugarHathi GPS</h1>
              <p className="text-xs text-orange-100">Live Location Sharing</p>
            </div>
          </div>
        </div>

        {/* Vehicle Info */}
        <div className="px-6 py-4 bg-orange-50 border-b border-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Vehicle</p>
              <p className="text-base font-bold text-gray-800 font-mono">{vehicleNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Driver</p>
              <p className="text-base font-bold text-gray-800">{driverName}</p>
            </div>
          </div>
        </div>

        {/* Status Area */}
        <div className="px-6 py-6 space-y-4">
          {/* Status Indicator */}
          <div className={`rounded-xl p-4 flex items-center gap-3 ${
            status === 'sharing' ? 'bg-green-50 border border-green-200' :
            status === 'error' ? 'bg-red-50 border border-red-200' :
            status === 'stopped' ? 'bg-gray-50 border border-gray-200' :
            status === 'requesting'? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
              status === 'sharing' ? 'bg-green-500 animate-pulse' :
              status === 'error' ? 'bg-red-500' :
              status === 'stopped' ? 'bg-gray-400' :
              status === 'requesting'? 'bg-blue-500 animate-pulse' : 'bg-gray-300'
            }`} />
            <div className="flex-1">
              <p className={`text-sm font-semibold ${
                status === 'sharing' ? 'text-green-700' :
                status === 'error' ? 'text-red-700' :
                status === 'stopped' ? 'text-gray-600' :
                status === 'requesting'? 'text-blue-700' : 'text-gray-600'
              }`}>
                {status === 'idle' && 'Ready to share location'}
                {status === 'requesting' && 'Requesting GPS permission...'}
                {status === 'sharing' && '🟢 Sharing live location'}
                {status === 'error' && '❌ GPS Error'}
                {status === 'stopped' && 'Location sharing stopped'}
              </p>
              {status === 'error' && (
                <p className="text-xs text-red-500 mt-0.5">{errorMsg}</p>
              )}
              {status === 'sharing' && lastUpdate && (
                <p className="text-xs text-green-600 mt-0.5">
                  Last update: {lastUpdate?.toLocaleTimeString('en-IN')}
                </p>
              )}
            </div>
          </div>

          {/* Coordinates (when sharing) */}
          {coords && status === 'sharing' && (
            <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400 font-medium">Latitude</p>
                <p className="text-sm font-mono font-semibold text-gray-700">{coords?.latitude?.toFixed(6)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 font-medium">Longitude</p>
                <p className="text-sm font-mono font-semibold text-gray-700">{coords?.longitude?.toFixed(6)}</p>
              </div>
              {coords?.speed !== null && coords?.speed !== undefined && (
                <div>
                  <p className="text-xs text-gray-400 font-medium">Speed</p>
                  <p className="text-sm font-semibold text-gray-700">
                    {coords?.speed ? `${(coords?.speed * 3.6)?.toFixed(1)} km/h` : '0 km/h'}
                  </p>
                </div>
              )}
              {coords?.accuracy && (
                <div>
                  <p className="text-xs text-gray-400 font-medium">Accuracy</p>
                  <p className="text-sm font-semibold text-gray-700">±{coords?.accuracy?.toFixed(0)}m</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {(status === 'idle' || status === 'error' || status === 'stopped') && (
            <button
              onClick={startSharing}
              className="w-full py-4 rounded-xl text-white font-bold text-base transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
            >
              📍 Start Sharing Location
            </button>
          )}

          {status === 'requesting' && (
            <button disabled className="w-full py-4 rounded-xl text-white font-bold text-base bg-blue-400 cursor-not-allowed">
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Getting GPS...
              </span>
            </button>
          )}

          {status === 'sharing' && (
            <button
              onClick={stopSharing}
              className="w-full py-4 rounded-xl text-white font-bold text-base bg-red-500 hover:bg-red-600 transition-all active:scale-95"
            >
              ⏹ Stop Sharing
            </button>
          )}

          {/* Instructions */}
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
            <p className="text-xs font-semibold text-amber-700 mb-2">📋 Instructions</p>
            <ul className="text-xs text-amber-600 space-y-1">
              <li>• Keep this page open while driving</li>
              <li>• Allow location permission when prompted</li>
              <li>• Your location updates automatically</li>
              <li>• Buyer can see your live position on map</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">SugarHathi • Secure GPS Tracking</p>
        </div>
      </div>
    </div>
  );
};

export default DriverLocationPage;
